/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useCallback, type ComponentProps, type ReactNode } from 'react';

import { cssMap, jsx } from '@compiled/react';
import { defineMessages, useIntl } from 'react-intl';
import { di } from 'react-magnetic-di';

import {
	FullScreenModalDialog,
	ModalHeader,
	ModalTitle,
	ModalTransition,
} from '@atlaskit/modal-dialog/full-screen';

import { ChatMainHeader } from '../chat-header-refresh';
import { SplitLayout } from '../split-layout/SplitLayout';

type ChatHeaderProps = ComponentProps<typeof ChatMainHeader>;

const messages = defineMessages({
	defaultModalTitle: {
		id: 'ai-mate.conversation-assistant.full-screen-modal.default-modal-title-non-final',
		defaultMessage: 'Rovo chat',
		description:
			'Default title shown in the header of the conversation assistant full-screen modal when no custom title is supplied.',
	},
});

const styles = cssMap({
	body: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'stretch',
		flex: '1 1 auto',
		minHeight: '0',
		minWidth: '0',
		height: '100%',
		width: '100%',
	},
});

export type FullScreenModalProps = {
	isOpen: boolean;
	/**
	 * Notifies the parent when the modal would like to change its open state
	 * (Escape key, close button). Parents are expected to mirror the value
	 * back into `isOpen`; until they do, the modal stays in its previous
	 * state — `isOpen` is the single source of truth.
	 */
	onOpenChange: (isOpen: boolean) => void;
	/** Content rendered as the **left** (chat) pane of the inner `SplitLayout`. */
	modalContent: ReactNode;
	/**
	 * Preferred header configuration for full chat experiences. When supplied,
	 * the modal renders the real `ChatMainHeader` and hides the stock close button.
	 *
	 * `ChatMainHeader` depends on the conversation assistant provider tree
	 * (chat-store, UI-store, global-config, etc.), so callers should mount this
	 * modal under the same providers as the inline chat surface.
	 */
	chatHeaderProps?: ChatHeaderProps;
	/**
	 * Legacy title rendered inside the modal header when `chatHeaderProps` is
	 * not supplied. Ignored when `headerContent` is supplied.
	 */
	title?: ReactNode;
	/**
	 * Legacy custom header node. When supplied, it replaces the title and hides
	 * the stock close button.
	 */
	headerContent?: ReactNode;
	/**
	 * Useful for one-shot side effects (analytics, telemetry); for state
	 * sync, use `onOpenChange` instead.
	 */
	onClose?: () => void;
	/**
	 * When `true`, focus is restored to the element that was focused before
	 * the modal opened. Defaults to `true`.
	 */
	shouldReturnFocus?: boolean;
};

export const FullScreenModal = ({
	isOpen,
	onOpenChange,
	modalContent,
	chatHeaderProps,
	title,
	headerContent,
	onClose,
	shouldReturnFocus = true,
}: FullScreenModalProps): JSX.Element => {
	di(ChatMainHeader);
	const { formatMessage } = useIntl();

	const handleClose = useCallback(() => {
		onOpenChange(false);
		onClose?.();
	}, [onOpenChange, onClose]);

	const resolvedTitle = title ?? formatMessage(messages.defaultModalTitle);
	const shouldUseChatHeader = Boolean(chatHeaderProps);
	const resolvedChatHeaderProps = chatHeaderProps;
	const hasCloseButton = !shouldUseChatHeader && !headerContent;

	return (
		<ModalTransition>
			{isOpen && (
				<FullScreenModalDialog
					onClose={handleClose}
					shouldReturnFocus={shouldReturnFocus}
					testId="conversation-assistant-full-screen-modal-dialog"
				>
					<ModalHeader hasCloseButton={hasCloseButton}>
						{resolvedChatHeaderProps ? (
							<ChatMainHeader {...resolvedChatHeaderProps} />
						) : (
							(headerContent ?? <ModalTitle>{resolvedTitle}</ModalTitle>)
						)}
					</ModalHeader>
					{/* Using a custom modal body here to ensure the each panel in the split layout is independently scrollable */}
					<div css={styles.body} data-testid="conversation-assistant-full-screen-modal-body">
						<SplitLayout>{modalContent}</SplitLayout>
					</div>
				</FullScreenModalDialog>
			)}
		</ModalTransition>
	);
};
